import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, OfflineError } from './api.ts';

export function Card({ title, actions, children, className = '' }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-h">
          {title && <h2>{title}</h2>}
          {actions && <div className="row gap">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span className="field-l">{label}</span>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

export function Badge({ tone = 'neutral', dot = false, children }: { tone?: 'neutral' | 'ok' | 'warn' | 'bad' | 'info'; dot?: boolean; children: ReactNode }) {
  return <span className={`badge ${tone}${dot ? ' has-dot' : ''}`}>{children}</span>;
}

/** Small outlined label for a record: ARREST, EVIDENCE, STOLEN… */
export function Flag({ tone = 'muted', children }: { tone?: 'bad' | 'info' | 'warn' | 'muted'; children: ReactNode }) {
  return <span className={`flag ${tone}`}>{children}</span>;
}

/** Page title with breadcrumb and actions on the right — the top of every screen. */
export function PageHeader({ title, crumb, actions, sub }: { title: ReactNode; crumb?: ReactNode; actions?: ReactNode; sub?: ReactNode }) {
  return (
    <header className="page-h">
      <div>
        {crumb && <div className="crumb">{crumb}</div>}
        <h1>{title}</h1>
        {sub && <div className="muted small" style={{ marginTop: 2 }}>{sub}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

const sentence = (s: string) => {
  const t = s.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const STATUS_TONE: Record<string, 'neutral' | 'ok' | 'warn' | 'bad' | 'info'> = {
  INTAKE: 'info', REVIEWED: 'info', READY: 'info', ASSIGNED: 'neutral', IN_SERVICE: 'warn', PENDING_REVIEW: 'bad',
  SERVED: 'ok', NON_SERVED: 'warn', AFFIDAVIT_SIGNED: 'ok', RETURNED: 'ok', CLOSED: 'neutral', ARCHIVED: 'neutral',
};
export function StatusBadge({ status, labels }: { status: string; labels?: Record<string, string> }) {
  return (
    <Badge tone={STATUS_TONE[status] ?? 'neutral'} dot>
      {labels?.[status] ?? sentence(status)}
    </Badge>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error as ApiError;
  const msg = error instanceof OfflineError
    ? navigator.onLine
      ? 'Can’t reach the CivilHound server. It may be stopped or restarting — try again in a moment.'
      : 'You are offline. This action needs a connection.'
    : e.message ?? String(error);
  const list: string[] = e.details?.missing ?? e.details?.errors ?? e.details?.blockers?.map((b: { message: string }) => b.message) ?? [];
  return (
    <div className="alert bad" role="alert">
      <strong>{msg}</strong>
      {list.length > 0 && (
        <ul>
          {list.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
      {e.details?.duplicates && (
        <ul>
          {e.details.duplicates.map((d: { civilos_number: string; reason: string; defendant: string }) => (
            <li key={d.civilos_number}>
              {d.civilos_number} — {d.defendant} ({d.reason})
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'ok' | 'bad'; children: ReactNode }) {
  return <div className={`alert ${tone}`}>{children}</div>;
}

/** Row actions behind one "Actions" button, so tables stay readable. */
export function RowMenu({ label = 'Actions', items }: { label?: string; items: ({ label: string; onClick: () => void; danger?: boolean } | false | null | undefined)[] }) {
  const [open, setOpen] = useState(false);
  const shown = items.filter(Boolean) as { label: string; onClick: () => void; danger?: boolean }[];
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => (clearTimeout(t), document.removeEventListener('click', close));
  }, [open]);
  if (!shown.length) return null;
  return (
    <span className="rowmenu">
      <button type="button" className="btn sm" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {label} <span aria-hidden className="caret" />
      </button>
      {open && (
        <span className="rowmenu-pop" role="menu">
          {shown.map((i) => (
            <button key={i.label} type="button" role="menuitem" className={i.danger ? 'danger' : ''} onClick={i.onClick}>
              {i.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

/** A file picker you can also drop a file onto. The real input stays inside, so keyboards and tests use it as usual. */
export function FileDrop({ accept, file, onFile, label, hint }: { accept?: string; file: File | null; onFile: (f: File | null) => void; label: string; hint?: ReactNode }) {
  const [over, setOver] = useState(false);
  return (
    <label
      className={`filedrop${over ? ' over' : ''}${file ? ' has' : ''}`}
      onDragOver={(e) => (e.preventDefault(), setOver(true))}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
    >
      <input type="file" accept={accept} aria-label={label} onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
      <span className="fd-main">{file ? file.name : <>Drop the file here, or <u>choose a file</u></>}</span>
      <span className="fd-hint">{file ? `${Math.round(file.size / 1024).toLocaleString()} KB · choose another to replace it` : hint}</span>
    </label>
  );
}

/** True on a phone-width screen (lists switch from tables to cards). */
export function useNarrow(query = '(max-width: 760px)') {
  const get = () => typeof matchMedia === 'function' && matchMedia(query).matches;
  const [v, setV] = useState(get);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia(query);
    const on = () => setV(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return v;
}

/** Tiny data hook. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fn()
      .then((d) => alive && (setData(d), setError(null)))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

/** Wrap an async action with busy/error state. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, setError };
}

/** Loading: the shape of what's coming (a few placeholder lines), not a spinning wheel. */
export function Spinner({ lines = 4 }: { lines?: number }) {
  return (
    <div className="skel" role="status" aria-label="Loading">
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} style={{ width: `${[92, 78, 85, 64, 88, 70][i % 6]}%` }} />
      ))}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}
